import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";

import { updateFunction, trackBtnClick, handleAndTrackError } from "@/lib/helper";
import Div100vh from "react-div-100vh";
import { motion, AnimatePresence } from "framer-motion";
import DynamicInputForm from "../common/DynamicInputForm";
import { onboardingFormDataAtom } from "@/recoil/atom/onboardingFormDataAtom";
import Searching from "@/assets/images/currently_searching.png";
import BusinessTravel from "@/assets/icons/found.svg";
import EmptyList from "@/assets/icons/noplans.svg";
import Done from "@/assets/images/found_house.png";
import Exploring from "@/assets/images/exploring-club.png";
import PrimaryButton from "@/components/common/buttons/PrimaryButton.jsx";
import GoBackButton from "../common/buttons/GoBackButton";
import Header from "../common/Header/header";
import IconChips from "../common/IconChips";
import TextField from "../common/Input/TextField";
import Loader from "../common/Loader";
import { Trash2, X } from "lucide-react";

const HEADING = "Current renting status?";
const options = [
  {
    heading: "Finalized a new home",
    value: "HOT",
    icon: Done,
    color: "border-2 border-primary-100 text-[#55C200]",
  },
  {
    heading: "Currently Searching for a home",
    value: "WARM",
    icon: Searching,
    color: "border-2 border-primary-100 text-[#F19932]",
  },
  {
    heading: "Exploring the Club",
    value: "COLD",
    icon: Exploring,
    color: "border-2 border-primary-100 text-[#1987fb]",
  },
];

const moveInOptions = [
  {
    type: "chipgroup",
    label: "When are you planning to move-in?",
    name: "move_in_plan",
    options: [
      {
        heading: "Immediate",
        value: "immediately",
        color: "bg-primary-100 text-black",
      },
      {
        heading: "Future",
        value: "in_future",
        color: "bg-primary-100 text-black",
      },
    ],
    required: true,
  },
];

const cityOptions = [
  "Gurugram",
  "Bengaluru",
  "Delhi",
  "Pune",
  "Mumbai",
  "Others",
];

export default function RentingStatusScreen() {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useRecoilState(onboardingFormDataAtom);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedMoveIn, setSelectedMoveIn] = useState(null);
  const [showMoveInOptions, setShowMoveInOptions] = useState(false);
  const [selectedCity, setSelectedCity] = useState(formData.moveInCity || "");
  const [customCity, setCustomCity] = useState("");

  const handleSelectStatus = (item) => {
    setSelectedStatus(item);
    setFormData({ ...formData, prospect: item.value });
    trackBtnClick("Renting Cards", "Renting Status Screen");

    // Show move-in options or proceed based on the status
    if (["HOT", "WARM", "COLD"].includes(item.value)) {
      setShowMoveInOptions(true);
    } else if (item.value === "COLD") {
      handleNextButton("COLD");
    }
  };

  const getRentPlaceholder = () => {
    if (selectedStatus?.value === "WARM") {
      return "Expected monthly rent";
    } else if (selectedStatus?.value === "HOT") {
      return "Finalized monthly rent";
    } else if (selectedStatus?.value === "COLD") {
      return "Present monthly rent";
    }
    return "Rent";
  };
  // const handleSelectMoveIn = (item) => {
  //   setSelectedMoveIn(item);
  //   setFormData({ ...formData, move_in_time: item.value });
  // };

  useEffect(() => {
    if (error) {
      setError("");
    }
  }, [formData]);

  const handleNextButton = async (value) => {
    try {
      // Validate required fields
      if (!formData.rent || !formData.income) {
        setError("Please enter all details");
        return;
      }
  
      setLoading(true);
  
      const prospect = formData.prospect || value;
  
      if (prospect && formData.income && formData.rent) {
        // Call updateFunction and handle errors centrally
        await updateFunction(
          {
            prospect: prospect,
            income: formData.income,
            rent_range: formData.rent,
          },
          "user/update",
          "Next",
          "Renting Status Screen"
        );
        
        navigate("/eligibility-check");
      } else {
        setError("Please enter all details");
      }
    } catch (error) {
      // Handle errors using centralized handler
      setError("Something went wrong. Please try again.");
      handleAndTrackError(error, {
        context: "Next Button Handler",
        formData,
        additionalValue: value,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChangeStatus = () => {
    setSelectedStatus(null);
    setSelectedMoveIn(null);
    setFormData({ ...formData, prospect: "", move_in_time: "" });
    setShowMoveInOptions(false);
  };

  const handleCityChange = (e) => {
    setSelectedCity(e.target.value);
    if (e.target.value !== "Others") {
      setFormData({ ...formData, moveInCity: e.target.value });
      setCustomCity("");
    } else {
      setFormData({ ...formData, moveInCity: "" });
    }
  };
  const rentOptions = [
    {
      heading: "Upto 10,000",
      value: "upto_10000",
      color: "bg-primary-100 text-black",
    },
    {
      heading: "10,000-25,000",
      value: "10000_25000",
      color: "bg-primary-100 text-black",
    },
    {
      heading: "25,000+",
      value: "above_25000",
      color: "bg-primary-100 text-black",
    },
  ];

  const handleSelectRent = (item) => {
    setFormData({ ...formData, rent: item.value });
  };
  const handleCustomCityChange = (e) => {
    setCustomCity(e.target.value);
    setFormData({ ...formData, moveInCity: e.target.value });
  };

  return (
    <Div100vh className="flex flex-col justify-between p-6 sm:p-8">
      <div className="flex flex-col gap-1 h-full">
        <Header
          progress="1"
          total="3"
          customBackSubmit={
            selectedStatus ? () => handleChangeStatus() : undefined
          }
        />
        <div className="flex flex-col gap-2 pt-2">
          <span className="heading mb-heading-subheading">{HEADING}</span>
        </div>
        <AnimatePresence>
          {!showMoveInOptions && (
            <motion.div
              className="grid grid-cols-1 gap-4 mb-4 py-3 w-full"
              initial={{ opacity: 0, y: 0 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: { duration: 0.3, ease: "easeInOut" },
              }}
              exit={{
                opacity: 0,
                y: -30,
                transition: { duration: 0.2, ease: "easeInOut" },
              }}
            >
              {options.map((option) => (
                <IconChips
                  key={option.value}
                  item={option}
                  isSelected={selectedStatus?.value === option.value}
                  onSelect={() => handleSelectStatus(option)}
                  variant={"icon"}
                  showRevampDesign
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence>
          {showMoveInOptions && (
            <div className="">
              <motion.div
                className="flex w-full items-center mt-6 bg-[#222] rounded-full justify-center"
                initial={{ opacity: 0, y: 0 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, ease: "easeInOut" },
                }}
                exit={{
                  opacity: 0,
                  y: 50,
                  transition: { duration: 0.2, ease: "easeInOut" },
                }}
              >
                <div className="w-full">
                  <IconChips
                    item={selectedStatus}
                    isSelected={true}
                    variant={"small-icon"}
                  />
                </div>
                <button
                  className="pl-2 pr-5 text-xs"
                  onClick={handleChangeStatus}
                >
                  <X size={20}/>
                </button>
              </motion.div>
              {showMoveInOptions && (
                <motion.div
                  className="mt-6"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.6, ease: "easeInOut" },
                  }}
                  exit={{
                    opacity: 0,
                    y: 10,
                    transition: { duration: 0.5, ease: "easeInOut" },
                  }}
                >
                  <div className="grid grid-cols-1 gap-2 mt-4 mb-8">
                    {/* Rent Dropdown */}
                    <span className="subheading block">
                      {getRentPlaceholder()}
                    </span>
                    <div className="relative w-full">
                      <select
                        value={formData.rent || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, rent: e.target.value })
                        }
                        required
                        className="w-full bg-[#111111] border px-4 py-2.5 pr-10 border-primary-500 h-14 text-white shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ease-in-out appearance-none"
                      >
                        <option value="" disabled>
                          Select an option
                        </option>
                        {[
                          { value: "upto_10000", label: "Upto ₹10,000" },
                          { value: "10000_25000", label: "₹10,000 - ₹25,000" },
                          { value: "above_25000", label: "₹25,000+" },
                        ].map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <svg
                        className="w-3 h-3 absolute top-1/2 right-4 transform -translate-y-1/2 text-white pointer-events-none"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 10 6"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 4 4 4-4"
                        />
                      </svg>
                    </div>

                    <br />
                    <span className="subheading block">
                      Monthly Income
                    </span>
                    <div className="relative w-full">
                      <select
                        value={formData.income || ""}
                        onChange={(e) =>
                          setFormData({ ...formData, income: e.target.value })
                        }
                        required
                        className="w-full bg-[#111111] border px-4 py-2.5 pr-10 border-primary-500 h-14 text-white shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ease-in-out appearance-none"
                      >
                        <option value="" disabled>
                          Select an option
                        </option>
                        {[
                          { value: "upto_25000", label: "Upto ₹25,000" },
                          { value: "25000_40000", label: "₹25,000 - ₹40,000" },
                          { value: "above_40000", label: "₹40,000+" },
                        ].map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <svg
                        className="w-3 h-3 absolute top-1/2 right-4 transform -translate-y-1/2 text-white pointer-events-none"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 10 6"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 4 4 4-4"
                        />
                      </svg>
                    </div>

                    {/* Move-in City Dropdown */}
                    {/* <br /> */}
                    {/* <span className="subheading block">
                      Select Move-in City
                    </span>
                    <div className="relative w-full">
                      <select
                        value={selectedCity}
                        onChange={handleCityChange}
                        className="w-full bg-[#111111] border px-4 py-2.5 pr-10 border-primary-500 h-14 text-white shadow-sm focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 ease-in-out appearance-none"
                      >
                        <option value="" disabled>
                          Choose from options
                        </option>
                        {cityOptions.map((city) => (
                          <option key={city} value={city}>
                            {city}
                          </option>
                        ))}
                      </select>
                      <svg
                        className="w-3 h-3 absolute top-1/2 right-4 transform -translate-y-1/2 text-white pointer-events-none"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 10 6"
                      >
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="m1 1 4 4 4-4"
                        />
                      </svg>
                    </div> */}

                    {/* {selectedCity === "Others" && (
                      <input
                        type="text"
                        placeholder="Enter your city"
                        value={customCity}
                        onChange={handleCustomCityChange}
                        className="py-2 px-4 border rounded-md mt-2 bg-[#111111] text-white focus:outline-none focus:ring-primary-500 focus:border-primary-500 h-14"
                      />
                    )} */}
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </AnimatePresence>
      </div>
      {showMoveInOptions && (
        <div className="">
          <PrimaryButton
            handleClick={handleNextButton}
            text="Next"
            className="w-full"
            error={error}
            loading={loading}
          />
        </div>
      )}
    </Div100vh>
  );
}
